<template v-if="mapped.valid && ready">
    <div class="map-wrap" :class="{ 'hide-markers': !showStations }">
        <template v-if="!isCustomisationEnabled()">
            <StationsMapHeader v-if="showHeader" :project="project"></StationsMapHeader>
            <StationsMapSidebar
                v-if="showSidebar"
                :mapped="mapped"
                :stations="filteredStations"
                @update-results-based-on-map="getStationsForBounds"
                @select-station="$emit('show-summary', { id: $event })"
                @toggle="handleLayoutChanges()"
            ></StationsMapSidebar>
            <slot></slot>
        </template>
        <mapbox
            class="stations-map"
            :access-token="mapbox.token"
            :map-options="{
                style: mapbox.style,
                bounds: bounds,
                zoom: 10,
            }"
            :nav-control="{
                show: !isMobileView,
                position: 'bottom-left',
            }"
            @map-init="onMapInitialized"
            @map-load="onMapLoaded"
            @zoomend="newBounds"
            @dragend="newBounds"
        />
    </div>
</template>

<script lang="ts">
/* eslint-disable vue/no-unused-components */

import _ from "lodash";
import Config from "@/secrets";
import { MappedStations, LngLat, BoundingRectangle, VisibleReadings, DisplayProject, DisplayStation } from "@/store";

import mapboxgl from "mapbox-gl";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";

import Vue, { PropType } from "vue";
import ValueMarker from "./ValueMarker.vue";
import OfflineMarker from "./partners/OfflineMarker.vue";
import MaintenanceMarker from "./partners/MaintenanceMarker.vue";
import CommunityMarker from "./partners/CommunityMarker.vue";
import Mapbox from "mapbox-gl-vue";
import StationsMapHeader from "@/views/shared/StationsMapHeader.vue";
import StationsMapSidebar from "@/views/shared/StationsMapSidebar.vue";
import { isCustomisationEnabled } from "@/views/shared/partners";
import { isDownStation, isRetiredStation, isCommunityStation } from "@/utilities/station-status";

export interface ProtectedData {
    map: any;
    markers: { marker: mapboxgl.Marker; instance: any }[];
}

export default Vue.extend({
    name: "StationsMap",
    components: {
        StationsMapSidebar,
        StationsMapHeader,
        Mapbox,
        ValueMarker,
        OfflineMarker,
        MaintenanceMarker,
        CommunityMarker,
    },
    data(): {
        mapbox: { token: string; style: string };
        ready: boolean;
        sensorMeta: Map<string, any> | null;
        geocoderAdded: boolean;
        isMobileView: boolean;
        filteredStations: DisplayStation[];
        filterStationsBasedOnMap: boolean;
        pendingBoundsToFit: LngLat[] | null;
    } {
        return {
            mapbox: Config.mapbox,
            ready: false,
            sensorMeta: null,
            geocoderAdded: false,
            isMobileView: window.screen.availWidth <= 768,
            filteredStations: this.mapped.stations,
            filterStationsBasedOnMap: false,
            pendingBoundsToFit: null,
        };
    },
    props: {
        mapped: {
            type: MappedStations,
        },
        value: {
            type: BoundingRectangle,
        },
        mapBounds: {
            type: BoundingRectangle,
        },
        showStations: {
            type: Boolean,
            default: false,
        },
        layoutChanges: {
            type: Number,
            default: 0,
        },
        visibleReadings: {
            type: Number as PropType<VisibleReadings>,
            default: VisibleReadings.Current,
        },
        project: {
            type: Object as () => DisplayProject,
            required: false,
        },
        showSidebar: {
            type: Boolean,
            default: false,
        },
        showHeader: {
            type: Boolean,
            default: false,
        },
        enableGeocoder: {
            type: Boolean,
            default: null,
        },
    },
    computed: {
        // Mapbox maps absolutely hate being mangled by Vue
        protectedData(): ProtectedData {
            return this as unknown as ProtectedData;
        },
        bounds(): LngLat[] | null {
            if (this.value) {
                return this.value.lngLat();
            }

            return this.mapBounds ? this.mapBounds.lngLat() : this.mapped.boundsLngLat();
        },
    },
    watch: {
        layoutChanges(): void {
            this.handleLayoutChanges();
        },
        mapped(): void {
            console.log("map: mapped changed", this.mapped);
            this.updateMap();
        },
        visibleReadings(): void {
            console.log("map: visible-readings");
            this.updateMap();
        },
        showStations(): void {
            if (!this.protectedData.map.getLayer("station-markers")) {
                return;
            }
            if (!this.showStations) {
                this.protectedData.map.setLayoutProperty("station-markers", "visibility", "none");
                return;
            }
            this.protectedData.map.setLayoutProperty("station-markers", "visibility", "visible");
        },
    },
    methods: {
        shouldEnableGeocoder(): boolean {
            if (this.enableGeocoder != null) {
                return this.enableGeocoder;
            }
            return isCustomisationEnabled();
        },
        isCustomisationEnabled,
        onMapInitialized(map: any): void {
            console.log("map: initialized");
            this.protectedData.map = map;
        },
        onMapLoaded(map: any): void {
            console.log("map: loaded");
            this.protectedData.map = map;

            if (!map.hasImage("dot")) {
                const compass = this.$loadAsset("Icon_Map_Dot.png");
                map.loadImage(compass, (error, image) => {
                    if (error) throw error;
                    if (!map.hasImage("dot")) {
                        map.addImage("dot", image);
                    }
                });
            }

            setTimeout(() => {
                map.resize();

                this.ready = true;
                this.updateMap();

                // Force model to update.
                this.newBounds();
            }, 100);
        },
        newBounds() {
            const map = this.protectedData.map;
            const bounds = map.getBounds();
            this.filterStationsForBounds();
            this.$emit("input", new BoundingRectangle([bounds._sw.lng, bounds._sw.lat], [bounds._ne.lng, bounds._ne.lat]));
        },
        updateMap(): void {
            if (!this.protectedData.map) {
                console.log("map: update-skip.1");
                return;
            }

            if (!this.mapped || !this.mapped.valid || !this.ready) {
                console.log("map: update-skip.2", this.mapped?.valid, this.ready);
                return;
            }

            const map = this.protectedData.map;

            if (!this.geocoderAdded && this.shouldEnableGeocoder()) {
                map.addControl(
                    new MapboxGeocoder({
                        accessToken: this.mapbox.token,
                        mapboxgl: mapboxgl,
                        collapsed: true,
                        marker: false,
                    }),
                    "top-left"
                );
                this.geocoderAdded = true;
            }

            const stationsSource = map.getSource("stations");
            if (!stationsSource && this.showStations) {
                console.log("map: updating", this.mapped);

                map.addSource("stations", {
                    type: "geojson",
                    data: {
                        type: "FeatureCollection",
                        features: this.mapped.features,
                    },
                });

                map.addLayer({
                    id: "regions",
                    type: "fill",
                    source: "stations",
                    paint: {
                        "fill-color": "#aaaaaa",
                        "fill-opacity": 0.2,
                    },
                    filter: ["==", "$type", "Polygon"],
                });

                if (!this.mapped.isSingleType) {
                    map.addLayer({
                        id: "station-markers",
                        type: "symbol",
                        source: "stations",
                        filter: ["==", "$type", "Point"],
                        layout: {
                            "icon-image": "dot",
                            "text-field": "{title}",
                            "icon-ignore-placement": true,
                            "icon-allow-overlap": true,
                            "text-allow-overlap": true,
                            "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
                            "text-offset": [0, 0.75],
                            "text-variable-anchor": ["top", "right", "bottom", "left"],
                        },
                    });
                }

                map.on("click", "station-markers", (e) => {
                    const id = e.features[0].properties.id;
                    console.log("map: click", id);
                    this.$emit("show-summary", { id: id, preserveBounds: true });
                });
            } else if (stationsSource) {
                console.log("map: updating source data", this.mapped);
                stationsSource.setData({
                    type: "FeatureCollection",
                    features: this.mapped.features,
                });
            }

            if (this.bounds) {
                this.pendingBoundsToFit = this.bounds;
            }

            // Regenerate custom map markers
            if (this.protectedData.markers) {
                for (const marker of this.protectedData.markers) {
                    marker.marker.remove();
                }
                this.protectedData.markers = [];
            }

            const ValueMarkerCtor = Vue.extend(ValueMarker) as typeof Vue;
            const OfflineMarkerCtor = Vue.extend(OfflineMarker) as typeof Vue;
            const MaintenanceMarkerCtor = Vue.extend(MaintenanceMarker) as typeof Vue;
            const CommunityMarkerCtor = Vue.extend(CommunityMarker) as typeof Vue;
            const markers: { marker: mapboxgl.Marker; instance: any }[] = [];
            const sortFactors = _.fromPairs(
                this.mapped.features.map((feature) => [feature.properties?.id, feature.station.getSortOrder(this.visibleReadings)])
            );

            const sorted = _.reverse(
                _.orderBy(
                    _.cloneDeep(this.mapped.features),
                    [
                        (feature) => (feature.properties != null ? sortFactors[feature.properties.id][0] : 0),
                        (feature) => (feature.properties != null ? sortFactors[feature.properties.id][1] : 0),
                        (feature) => (feature.properties != null ? sortFactors[feature.properties.id][2] : 0),
                    ],
                    ["asc", "desc", "asc"]
                )
            );
            for (const feature of sorted) {
                if (feature.geometry != null && feature.properties != null) {
                    const readings = feature.station.inactive ? null : feature.station.getDecoratedReadings(this.visibleReadings);

                    const markerInstance = this.createMarkerInstance(feature, readings, {
                        ValueMarkerCtor,
                        OfflineMarkerCtor,
                        MaintenanceMarkerCtor,
                        CommunityMarkerCtor,
                    });

                    markerInstance.$mount();
                    markerInstance.$on("marker-click", (evt) => {
                        this.$emit("show-summary", { id: evt.id, preserveBounds: true });
                    });

                    const marker = new mapboxgl.Marker(markerInstance.$el as HTMLElement)
                        .setLngLat(feature.geometry.coordinates)
                        .addTo(map);
                    markers.push({ marker: marker, instance: markerInstance });
                }
            }
            this.protectedData.markers = markers;
        },
        createMarkerInstance(
            feature: any,
            readings: any,
            markerCtors: {
                ValueMarkerCtor: typeof Vue;
                OfflineMarkerCtor: typeof Vue;
                MaintenanceMarkerCtor: typeof Vue;
                CommunityMarkerCtor: typeof Vue;
            }
        ): any {
            const { ValueMarkerCtor, OfflineMarkerCtor, MaintenanceMarkerCtor, CommunityMarkerCtor } = markerCtors;

            const baseProps = {
                id: feature.properties.id,
            };

            // For non-customized projects, use ValueMarker always
            if (!this.isCustomisationEnabled()) {
                return new ValueMarkerCtor({
                    propsData: {
                        ...baseProps,
                        ...(readings && readings.length > 0 && { color: readings[0].color }),
                        value: readings && readings.length > 0 ? readings[0].value : null,
                    },
                });
            }

            if (isCommunityStation(feature.station)) {
                return new CommunityMarkerCtor({ propsData: baseProps });
            }

            if (isDownStation(feature.station)) {
                return new MaintenanceMarkerCtor({ propsData: baseProps });
            }

            if (isRetiredStation(feature.station)) {
                return new OfflineMarkerCtor({ propsData: baseProps });
            }

            return new ValueMarkerCtor({
                propsData: {
                    ...baseProps,
                    ...(readings && readings.length > 0 && { color: readings[0].color }),
                    value: readings && readings.length > 0 ? readings[0].value : null,
                },
            });
        },
        getStationsForBounds(isChecked: boolean) {
            this.filterStationsBasedOnMap = isChecked;

            if (this.filterStationsBasedOnMap) {
                this.filterStationsForBounds();
                return;
            }
            this.filteredStations = this.mapped.stations;
        },
        filterStationsForBounds() {
            if (!this.filterStationsBasedOnMap) {
                return;
            }

            const bounds = this.protectedData.map.getBounds();
            const sw = bounds._sw;
            const ne = bounds._ne;

            this.filteredStations = [];

            this.mapped.stations.forEach((station) => {
                if (!station.location) {
                    return;
                }
                const lat = station.location.latitude;
                const lng = station.location.longitude;
                if (lat >= sw.lat && lat <= ne.lat && lng >= sw.lng && lng <= ne.lng) {
                    this.filteredStations.push(station);
                }
            });
        },
        handleLayoutChanges(): void {
            console.log("map: layout changed");
            if (this.protectedData.map) {
                // need timeout because css animation needs time to complete
                this.$nextTick(() => {
                    setTimeout(() => {
                        this.protectedData.map.resize();

                        if (this.pendingBoundsToFit) {
                            this.protectedData.map.fitBounds(this.pendingBoundsToFit, {
                                duration: 0,
                                padding: 50,
                                maxZoom: 14,
                            });
                            this.pendingBoundsToFit = null;
                        }
                    }, 250);
                });
            }
        },
        recenterOnStation(stationId: number): void {
            const station = this.mapped.stations.find((s) => s.id === stationId);
            if (!station || !station.location) {
                return;
            }

            const map = this.protectedData.map;
            if (!map) {
                return;
            }

            const focused = this.mapped.focusOn(stationId);
            if (focused.bounds) {
                const bounds = focused.bounds.lngLat();
                map.fitBounds(bounds, {
                    duration: 0,
                    padding: 50,
                    maxZoom: 14,
                });
            }
        },
    },
});
</script>

<style scoped lang="scss">
@use "src/scss/global";
@use "src/scss/mixins";
@use "src/scss/variables";

.map-view #map {
    height: calc(100% - 88px);
    position: relative;

    @include mixins.bp-down(variables.$lg) {
        height: 100%;
    }
}
.project-container #map {
    height: inherit;
    width: inherit;
}
.marker {
    height: 10px;
    width: 10px;
}

::v-deep .mapboxgl-ctrl-geocoder {
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.13);
    border: solid 1px #f4f5f7;
    border-radius: 0;
    height: 40px;

    @include mixins.bp-down(variables.$sm) {
        width: 40px;
    }

    &.mapboxgl-ctrl-geocoder--collapsed {
        min-width: 40px;
    }

    &:not(.mapboxgl-ctrl-geocoder--collapsed) {
        @include mixins.bp-down(variables.$xs) {
            min-width: calc(100vw - 20px) !important;
        }
    }

    input {
        outline: none;
        height: 37px;
        padding-left: 38px;
        font-size: 16px;
    }
}

::v-deep .mapboxgl-ctrl-geocoder--icon-search {
    top: 9px;
    left: 8px;
}

::v-deep .mapboxgl-ctrl-geocoder--icon-close {
    margin-top: 4px;

    @include mixins.bp-down(variables.$sm) {
        margin-top: 3px;
    }
}

.map-wrap {
    height: 100%;
    display: flex;
    flex-wrap: wrap;

    &.hide-markers ::v-deep .mapboxgl-marker {
        display: none;
    }
}

.stations-map {
    height: 100%;
    flex: 1 1 auto;
}
</style>
